import React, { useContext, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { IntlContext } from "context";
import { adKeys, useApplyToOurAd } from "hooks/ad";
import { useApplyThreadOffer, threadKeys } from "hooks/thread";
import { useServiceMinSalary } from "hooks/service";
import { AdFrequency, AdInterface } from "types/ad";
import { ServiceInterface } from "types/service";
import { getServerMessage } from "lib/serverError";
import OfferPanelHeader from "./OfferPanelHeader";
import OfferMissionTypeSection from "./OfferMissionTypeSection";
import OfferPricingSection from "./OfferPricingSection";
import OfferStartDateSection from "./OfferStartDateSection";
import OfferMessageSection from "./OfferMessageSection";
import OfferSecurityNotice from "./OfferSecurityNotice";
import OfferPanelFooter from "./OfferPanelFooter";

type OfferPanelProps = {
    service: ServiceInterface;
    clientId: string;
    ad?: AdInterface;
    onApplied: () => void | Promise<void>;
    onClose: () => void;
};

type FormValues = {
    message: string;
    hourly_rate: string;
    duration: string;
    start_date: string;
    start_time: string;
};

const toTimeInputValue = (value?: string | null): string => {
    if (!value) return "";
    const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
    if (!match) return "";
    return `${match[1].padStart(2, "0")}:${match[2]}`;
};

const OfferPanel: React.FC<OfferPanelProps> = ({ ad, service, clientId, onApplied, onClose }) => {
    const intl = useContext(IntlContext);
    const queryClient = useQueryClient();
    const applyToAdMutation = useApplyToOurAd();
    const applyThreadOfferMutation = useApplyThreadOffer();
    const { data: minSalary = 0 } = useServiceMinSalary();
    const isDirectOffer = !ad;

    const initialValues: FormValues = useMemo(
        () => ({
            message: "",
            hourly_rate: String(service.price ?? ""),
            duration: ad?.duration ? String(ad.duration) : "",
            start_date: ad?.data?.start_date ?? "",
            start_time: toTimeInputValue(ad?.data?.start_time),
        }),
        [ad, service],
    );

    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};
        if (!values.duration || Number(values.duration) <= 0) {
            errors.duration = intl.formatMessage("booking.messages.offer.error.duration_required");
        }
        if (!values.hourly_rate || Number(values.hourly_rate) < minSalary) {
            errors.hourly_rate = intl.formatMessage("booking.messages.offer.error.hourly_rate_min", {
                min: String(minSalary),
            });
        }
        return errors;
    };

    const onSubmit = async (values: FormValues) => {
        const payload = {
            message: values.message,
            hourly_rate: values.hourly_rate,
            duration: values.duration,
            start_date: values.start_date || null,
            start_time: values.start_time || null,
            mission_type: AdFrequency.ONE_TIME,
        };

        try {
            if (isDirectOffer) {
                await applyThreadOfferMutation.mutateAsync({
                    serviceId: service.id,
                    clientId,
                    data: payload,
                });
            } else {
                await applyToAdMutation.mutateAsync({
                    adId: ad.id,
                    serviceId: service.id,
                    data: payload,
                });
                await queryClient.invalidateQueries({ queryKey: adKeys.ourDetail(ad.id) });
                await queryClient.invalidateQueries({ queryKey: adKeys.ourApplications() });
            }

            await queryClient.invalidateQueries({ queryKey: threadKeys.offers(service.id, clientId) });
            await onApplied();
        } catch (err) {
            return {
                [FORM_ERROR]: getServerMessage(err) ?? intl.formatMessage("booking.messages.offer.error.submit_failed"),
            };
        }
    };

    return (
        <Form<FormValues>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
            render={({ handleSubmit, values, submitError, submitting }) => {
                const amount = Number(values.hourly_rate) * Number(values.duration);

                return (
                    <form onSubmit={handleSubmit} className="flex h-full flex-col bg-white">
                        <OfferPanelHeader onClose={onClose} />

                        <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-8 py-8">
                            {/*<OfferMissionTypeSection />*/}
                            <OfferPricingSection amount={amount} minSalary={minSalary} />
                            <OfferStartDateSection required={!isDirectOffer} />
                            <OfferMessageSection isDirectOffer={isDirectOffer} />
                            <OfferSecurityNotice />

                            {submitError ? (
                                <p className="text-sm font-medium text-destructive" aria-live="polite">
                                    {submitError}
                                </p>
                            ) : null}
                        </div>

                        <OfferPanelFooter onClose={onClose} submitting={submitting} />
                    </form>
                );
            }}
        />
    );
};

export default OfferPanel;
